import React, { memo, useMemo } from 'react'
import { getSlotImage, Slot, SlotImg, SlotImgDevice } from '@bcraft-frontend-lib/slots'
import block from 'bem-cn'

import { LoadableImage } from 'components/LoadableImage'

import { useImageRetry } from './useImageRetry'

const b = block('slots-icon')

const getRetryImageUrl = (slotId: Slot['id'], device: SlotImgDevice, attempt: number) =>
  `${getSlotImage(slotId, device)}?_retry=${attempt}`

type SlotPreviewImageProps = {
  resetKey: string | number
  gameName: string
  imageDevice: SlotImgDevice
  isCatalogSlot: boolean
  slotId: Slot['id']
  imageUrl: string
  imageUrlWide?: string
  wide?: boolean
}

export const SlotPreviewImage = memo(
  ({
    resetKey,
    gameName,
    imageDevice,
    isCatalogSlot,
    slotId,
    imageUrl,
    imageUrlWide,
    wide,
  }: SlotPreviewImageProps) => {
    const { attempt, isError, isReady, onLoadError } = useImageRetry(resetKey)

    const retryDevice = useMemo(() => {
      if (attempt % 2 === 0) {
        return imageDevice
      }

      return imageDevice === 'mobile' ? 'desktop' : 'mobile'
    }, [attempt, imageDevice])

    const retryImageUrl = useMemo(
      () => (isCatalogSlot ? getRetryImageUrl(slotId, retryDevice, attempt) : `${imageUrl}?_retry=${attempt}`),
      [attempt, imageUrl, isCatalogSlot, retryDevice, slotId],
    )

    if (isError) {
      return <div className={b('img-plug')}>{gameName}</div>
    }

    if (!isReady) {
      return <div className={b('img-retry-placeholder')} />
    }

    if (wide && imageUrlWide) {
      return (
        <img
          key={`wide-${attempt}`}
          className={b('img', { wide: true })}
          src={`${imageUrlWide}?_retry=${attempt}`}
          alt={gameName}
          onError={onLoadError}
        />
      )
    }

    if (isCatalogSlot && attempt === 0) {
      return (
        <SlotImg
          key="catalog-0"
          slotId={slotId}
          device={imageDevice}
          setIsError={onLoadError}
          loadableImgProps={{
            className: b('img').toString(),
            classNameContainer: b('img-container-inner').toString(),
            animationType: 'smallToBig',
          }}
        />
      )
    }

    if (isCatalogSlot) {
      return (
        <LoadableImage
          key={`catalog-retry-${attempt}`}
          className={b('img').toString()}
          classNameContainer={b('img-container-inner').toString()}
          src={retryImageUrl}
          alt={gameName}
          onError={onLoadError}
          animationType="smallToBig"
        />
      )
    }

    return (
      <LoadableImage
        key={`legacy-${attempt}`}
        className={b('img').toString()}
        classNameContainer={b('img-container-inner').toString()}
        src={retryImageUrl}
        alt={gameName}
        onError={onLoadError}
        animationType="smallToBig"
      />
    )
  },
)
